import React, { useState, useEffect, useCallback, useRef } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { rowSelected, userCredentials, routeInfo } from "recoilState/GlobalState";

//Services
import { accesoGet, zonaGet, provinciaGet } from "services/services-mia/miakey";

// utils
import XGrid from "components/UI/Grid/XGrid";
import { HeaderButton, Buttons, Button } from "components";
import { Add, Cached, DeleteOutline, EditOutlined, FilterAltOffRounded } from "@mui/icons-material";

import { DeleteModal, InsertModal, QrModal, UpdateModal } from "./Actions";

// consts
import { accesos as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";

// styles
import s from "styles/pages/Pages.module.scss"
import Modal from "components/UI/Modal/Modal";
import { useService } from "components/route/ServiceRoute";

import qrImg from "../../../../assets/icons/DescargarQR.svg"
import Filters from "components/Filters";
import { useFormik } from "formik";

export default function Accesos() {
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [modalDetails, setModalDetails] = useState({ isOpen: false });

    const [provincias, setProvincias] = useState([]);
    const [zonas, setZonas] = useState([]);

    const { service } = useService();

    const { row } = useRecoilValue(rowSelected);
    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);
    const allRowsRef = useRef([]);

    const formik = useFormik({
        initialValues: {
            zona: "",
            acceso: "",
        },
        onSubmit: ({ zona, acceso }) => {
            let filtered = [...allRowsRef.current];

            if (zona) {
                filtered = filtered.filter(
                    (row) => String(row.gateZonaId) === String(zona)
                );
            }

            if (acceso) {
                filtered = filtered.filter((row) =>
                    row.gateLugarNombre?.toLowerCase().includes(acceso.toLowerCase())
                );
            }

            setRows(filtered);
        },
    });

    const resetFilters = () => {
        formik.resetForm();
        formik.setFieldValue("acceso", "");
        formik.setFieldValue("zona", "");
        setRows([...allRowsRef.current]);
    };

    const updateList = useCallback(async () => {
        setIsLoading(true);
        setRows([]);
        const { result } = await accesoGet({}, credentials);
        const mappedRows = (result.map((item) => {
            const {
                gateLugarId,
                gateLugarBeaconCuenta,
                gateLugarCalle,
                gateLugarCasa,
                gateLugarDeviceCuenta,
                gateLugarEsDefault,
                gateLugarLatitud,
                gateLugarLongitud,
                gateLugarMaximo,
                gateLugarMinimo,
                gateLugarNombre,
                gateLugarUUID,
                gateLugarPiso,
                gateLugarQRCuenta,
                gateLugarRadio,
                gateLugarUFH,
                gateLugarUID,
                gateLugarUnidad,
                gateLugarVersion,
                gateZonaId,
                gateZonaNombre,
                localidadId,
                localidadNombre,
                merchantId,
                merchantNombre,
                merchantIRurl,
                provinciaId,
                provinciaNombre,
                gateLugarCod,
                gateZonaGlobal,
                gateLugarGlobal
            } = item;

            return {
                id: gateLugarId,
                gateLugarId,
                gateLugarBeaconCuenta,
                gateLugarCalle,
                gateLugarCasa,
                gateLugarDeviceCuenta,
                gateLugarEsDefault,
                gateLugarLatitud,
                gateLugarLongitud,
                gateLugarMaximo,
                gateLugarMinimo,
                gateLugarNombre,
                gateLugarUUID,
                gateLugarPiso,
                gateLugarQRCuenta,
                gateLugarRadio,
                gateLugarUFH,
                gateLugarUID,
                gateLugarUnidad,
                gateLugarVersion,
                gateZonaId,
                gateZonaNombre,
                localidadId,
                localidadNombre,
                merchantId,
                merchantNombre,
                merchantIRurl,
                provinciaId,
                provinciaNombre,
                latitud: gateLugarLatitud,
                longitud: gateLugarLongitud,
                gateLugarCod,
                gateZonaGlobal,
                gateLugarGlobal,
                [titles[0].field]: gateLugarCod,
                [titles[1].field]: gateLugarNombre,
                [titles[2].field]: gateZonaNombre,
                [titles[3].field]: gateZonaGlobal === "Y" ? "Si" : "No",
                [titles[4].field]: provinciaNombre,
                [titles[5].field]: localidadNombre,
                [titles[6].field]: gateLugarCalle,
                [titles[7].field]: gateLugarCasa,
                [titles[8].field]: gateLugarPiso,
                [titles[9].field]: gateLugarUnidad,
                [titles[10].field]: gateLugarRadio,
            };
        }));
        allRowsRef.current = mappedRows;
        setRows(mappedRows);
        setIsLoading(false);
    }, [credentials]);

    useEffect(() => {
        setRoute(Routes.accesos)
    }, [setRoute])

    useEffect(() => {
        updateList()

    }, [service, updateList])

    useEffect(() => {
        if (Object.keys(credentials).length > 0 && service?.servicioCod) {
            (async () => {
                const { result: resultProvincias } = await provinciaGet({}, credentials);
                const { result: resultZonas } = await zonaGet({}, credentials)
                setProvincias(resultProvincias);
                setZonas(resultZonas);
            })();
        }
    }, [credentials, service])

    const handleClick = ({ procede, title }) => {
        setModalDetails({ procede, title, isOpen: true });
    };

    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };

    return (
        <main className={s.main}>
            <Modal onClose={closeModal} open={modalDetails.isOpen} title={modalDetails.title}>
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        acceso={row}
                        updateList={updateList}
                        closeModal={closeModal}
                        provincias={provincias}
                        zonas={zonas}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        acceso={row}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        updateList={updateList}
                        closeModal={closeModal}
                        provincias={provincias}
                        zonas={zonas}
                    />
                )}
                {modalDetails.procede === ACTIONS.QR && (
                    <QrModal
                        acceso={row}
                        servicio={service}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
            </Modal>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            { type: "text", name: "acceso", label: "Nombre de acceso", disabled: false },
                            {
                                type: "autocomplete",
                                name: "zona",
                                label: "Zona",
                                width: "225px",
                                disabled: false,
                                options: [{ gateZonaId: "", gateZonaNombre: "Todos" }, ...zonas],
                                firstOptionKey: "gateZonaId",
                                firstOptionName: "gateZonaNombre",
                                firstOptionValue: "gateZonaId",
                            },
                        ]}
                    />
                    <HeaderButton onClick={resetFilters}>
                        <FilterAltOffRounded />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={updateList}
                        disabled={row.id}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Generar QR"}
                        handleClick={handleClick}
                        data={{
                            title: "Generar QR",
                            procede: ACTIONS.QR
                        }}
                        disabled={!row.id || row?.gateLugarGlobal === 'Y'}
                    >
                        <img src={qrImg} style={{ width: "24px" }} alt="qr" />
                    </HeaderButton>
                    <HeaderButton
                        text={"Agregar Acceso"}
                        handleClick={handleClick}
                        data={{
                            title: "Agregar Acceso",
                            procede: ACTIONS.INSERT
                        }}
                        disabled={row.id || !service.servicioPermiso === 'E'}
                    >
                        <Add />
                    </HeaderButton>
                    {/* <HeaderButton
                        text={"Detalle del Acceso"}
                        handleClick={handleClick}
                        data={{
                            title: "Detalle del Acceso",
                            procede: ACTIONS.UPDATE
                        }}
                        disabled={!row.id || !service.servicioPermiso === 'E'}
                    >
                        <EditOutlined />
                    </HeaderButton> */}
                    <HeaderButton
                        text={"Editar Acceso"}
                        handleClick={handleClick}
                        data={{
                            title: "Editar Acceso",
                            procede: ACTIONS.UPDATE
                        }}
                        disabled={!row.id || !service.servicioPermiso === 'E'}
                    >
                        <EditOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Eliminar Acceso"}
                        handleClick={handleClick}
                        data={{
                            title: "Eliminar Acceso",
                            procede: ACTIONS.DELETE,
                        }}
                        disabled={!row.id || !service.servicioPermiso === 'E'}
                    >
                        <DeleteOutline />
                    </HeaderButton>
                </Buttons>
            </div>
            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                />
            </div>
        </main>
    );
}
